/**
 * Playground React Query Hooks
 * Data fetching and mutations for playground features
 *
 * Uses new /v1/images API endpoints
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { playgroundApi } from '@/lib/api/playground';
import { agentApi } from '@/lib/api/agents';
import type {
  GenerateImageRequest,
  ImageGenerationConfig,
  PromptGenerationRequest,
  ImageModelInfo,
  LuckyCharmRequest,
} from '@/types/playground';

/**
 * Query keys
 */
const QUERY_KEYS = {
  models: ['playground', 'models'] as const,
  modelsByOperation: (op: string) => ['playground', 'models', op] as const,
  tasks: ['playground', 'tasks'] as const,
  task: (id: string) => ['playground', 'tasks', id] as const,
  library: ['playground', 'library'] as const,
  agents: ['playground', 'agents'] as const,
  promptAssistTemplate: ['playground', 'prompt-assist', 'template'] as const,
  luckyCharmThemes: ['playground', 'lucky-charm', 'themes'] as const,
  luckyCharmImages: ['playground', 'lucky-charm', 'images'] as const,
  luckyCharmFonts: ['playground', 'lucky-charm', 'fonts'] as const,
};

// ============= Model Discovery Hooks =============

/**
 * Get all available image models
 */
export function useImageModels(operation?: 'generate' | 'edit' | 'fuse') {
  return useQuery({
    queryKey: operation ? QUERY_KEYS.modelsByOperation(operation) : QUERY_KEYS.models,
    queryFn: () => playgroundApi.models.list(operation),
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

// ============= Agent Hooks =============

/**
 * Simplified agent info for playground
 */
export interface PlaygroundAgent {
  agent_type: string;
  name: string;
  description?: string;
}

/**
 * Get active agents list for playground
 */
export function usePlaygroundAgents() {
  return useQuery({
    queryKey: QUERY_KEYS.agents,
    queryFn: async (): Promise<PlaygroundAgent[]> => {
      console.log('[usePlaygroundAgents] Fetching agents...');
      const response = await agentApi.list(false); // Only active agents
      console.log('[usePlaygroundAgents] Raw response:', response);

      const agents = (response.agents || []).map((agent) => ({
        agent_type: agent.agent_type,
        name: agent.configuration?.name || agent.agent_type,
        description: agent.configuration?.description || '',
      }));

      console.log('[usePlaygroundAgents] Mapped agents:', agents);
      return agents;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// ============= New Image API Hooks =============

/**
 * Get tasks (history)
 */
export function useTasks(params?: { type?: string; status?: string; limit?: number }) {
  return useQuery({
    queryKey: [...QUERY_KEYS.tasks, params],
    queryFn: () => playgroundApi.images.listTasks(params),
  });
}

/**
 * Get single task
 */
export function useTask(taskId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.task(taskId),
    queryFn: () => playgroundApi.images.getTask(taskId),
    enabled: !!taskId,
  });
}

/**
 * Generate image with new Image API
 */
export function useGenerateImageV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateImageRequest) => playgroundApi.images.generate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Edit image with new Image API
 */
export function useEditImageV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateImageRequest) => playgroundApi.images.edit(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Fuse images with new Image API
 */
export function useFuseImagesV2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GenerateImageRequest) => playgroundApi.images.fuse(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Generate Lucky Charm image
 * Supports two modes:
 * - direct: Use provided phrase to generate image
 * - chart: Generate personalized fortune from user's birth chart
 */
export function useLuckyCharmGenerate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LuckyCharmRequest) => playgroundApi.images.luckyCharm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Lucky Charm theme/pack info
 */
export interface LuckyCharmTheme {
  id: string;
  display_name: string;
  description: string;
  image_count: number;
  is_root?: boolean;
}

/**
 * Lucky Charm background image info
 */
export interface LuckyCharmImage {
  id: string;
  filename: string;
  display_name: string;
  theme?: string;
  theme_id?: string;
  emotion?: string;
  has_metadata: boolean;
}

/**
 * Lucky Charm font info
 */
export interface LuckyCharmFont {
  id: number;
  name: string;
  path: string;
  display_name?: string;
  description?: string;
  is_default?: boolean;
}

/**
 * Get available Lucky Charm themes/packs
 */
export function useLuckyCharmThemes() {
  return useQuery({
    queryKey: QUERY_KEYS.luckyCharmThemes,
    queryFn: async (): Promise<LuckyCharmTheme[]> => {
      const response = await playgroundApi.images.getLuckyCharmThemes();
      return response.themes || [];
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Get available Lucky Charm background images
 * @param themeId - Optional theme ID to filter images
 */
export function useLuckyCharmImages(themeId?: string) {
  return useQuery({
    queryKey: [...QUERY_KEYS.luckyCharmImages, themeId],
    queryFn: async (): Promise<LuckyCharmImage[]> => {
      const response = await playgroundApi.images.getLuckyCharmImages(themeId);
      // Use enhanced image_list if available, fallback to simple images array
      if (response.image_list && response.image_list.length > 0) {
        return response.image_list;
      }
      // Fallback for backward compatibility
      return (response.images || []).map((filename) => ({
        id: filename,
        filename: filename,
        display_name: filename.replace(/\.(png|jpg|jpeg)$/i, ''),
        has_metadata: false,
      }));
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Get available Lucky Charm fonts
 */
export function useLuckyCharmFonts() {
  return useQuery({
    queryKey: QUERY_KEYS.luckyCharmFonts,
    queryFn: async (): Promise<{ fonts: LuckyCharmFont[]; defaultFontId: number | null }> => {
      const response = await playgroundApi.images.getLuckyCharmFonts();
      return {
        fonts: response.fonts || [],
        defaultFontId: response.default_font_id,
      };
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Delete task
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => playgroundApi.images.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Clear all tasks
 */
export function useClearTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => playgroundApi.images.clearTasks(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Save task to library (moves temp file - no re-download!)
 */
export function useSaveTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      taskId: string;
      category?: string;
      filename?: string;
      metadata?: Record<string, unknown>;
    }) => playgroundApi.images.saveTask(data.taskId, {
      category: data.category,
      filename: data.filename,
      metadata: data.metadata,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.library });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    },
  });
}

/**
 * Generate prompt with AI assistance
 */
export function usePromptAssist() {
  return useMutation({
    mutationFn: (data: PromptGenerationRequest) => playgroundApi.promptAssist.generate(data),
  });
}

/**
 * Get prompt assist system prompt templates (for transparency)
 * Shows what prompts are used in Raw LLM mode
 */
export function usePromptAssistTemplate() {
  return useQuery({
    queryKey: QUERY_KEYS.promptAssistTemplate,
    queryFn: () => playgroundApi.promptAssist.getTemplate(),
    staleTime: Infinity, // Template rarely changes
  });
}

// ============= Library Hooks =============

/**
 * Get library items
 */
export function useLibrary(params?: { category?: string; limit?: number }) {
  return useQuery({
    queryKey: [...QUERY_KEYS.library, params],
    queryFn: () => playgroundApi.library.list(params),
  });
}

/**
 * Save to library
 */
export function useSaveToLibrary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      image_url?: string;
      image_base64?: string;
      filename?: string;
      category?: string;
      metadata?: Record<string, unknown>;
    }) => playgroundApi.library.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.library });
    },
  });
}

/**
 * Delete from library
 */
export function useDeleteFromLibrary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => playgroundApi.library.delete(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.library });
    },
  });
}

// ============= Dynamic Config Hook =============

import { useState, useCallback, useMemo, useEffect } from 'react';
import { DEFAULT_CONFIG } from '@/types/playground';

/**
 * Hook for managing image generation config with dynamic model capabilities
 * Uses models from API instead of hardcoded values
 */
export function useDynamicImageConfig(
  models: ImageModelInfo[],
  operationMode: 'generate' | 'edit' | 'fuse' = 'generate'
) {
  const [config, setConfig] = useState<ImageGenerationConfig>(DEFAULT_CONFIG);

  // Get current model info from API models
  const currentModel = useMemo(
    () => models.find((m) => m.model_id === config.model),
    [models, config.model]
  );

  // Filter models by operation mode
  const availableModels = useMemo(() => {
    return models.filter((m) => {
      switch (operationMode) {
        case 'generate':
          return m.supports_generate;
        case 'edit':
          return m.supports_edit;
        case 'fuse':
          return m.supports_fuse;
        default:
          return true;
      }
    });
  }, [models, operationMode]);

  // Initialize with first available model if current model is not available
  useEffect(() => {
    if (availableModels.length > 0 && !availableModels.find((m) => m.model_id === config.model)) {
      const defaultModel = availableModels[0];
      setConfig((prev) => ({
        ...prev,
        model: defaultModel.model_id,
        size: defaultModel.default_size,
        image_size: defaultModel.default_size, // legacy alias
        aspect_ratio: defaultModel.default_aspect_ratio || prev.aspect_ratio,
        quality: defaultModel.default_quality,
        output_format: defaultModel.default_output_format,
      }));
    }
  }, [availableModels, config.model]);

  // Update model and reset options based on new model's capabilities
  const setModel = useCallback(
    (modelId: string) => {
      const newModel = models.find((m) => m.model_id === modelId);
      if (!newModel) return;

      setConfig((prev) => {
        // Check if current options are valid for new model
        const sizeValid = newModel.sizes.includes(prev.size);
        const aspectRatioValid = newModel.aspect_ratios.includes(prev.aspect_ratio);
        const qualityValid = !prev.quality || newModel.quality_options.includes(prev.quality);
        const newSize = sizeValid ? prev.size : newModel.default_size;

        return {
          ...prev,
          model: modelId,
          size: newSize,
          image_size: newSize, // legacy alias
          aspect_ratio: aspectRatioValid ? prev.aspect_ratio : (newModel.default_aspect_ratio || '1:1'),
          quality: qualityValid ? prev.quality : newModel.default_quality,
          output_format: newModel.output_formats.includes(prev.output_format)
            ? prev.output_format
            : newModel.default_output_format,
          // Reset web search if not supported
          web_search: newModel.supports_web_search ? prev.web_search : false,
        };
      });
    },
    [models]
  );

  const setSize = useCallback((size: string) => {
    setConfig((prev) => ({ ...prev, size, image_size: size }));
  }, []);

  const setAspectRatio = useCallback((aspect_ratio: string) => {
    setConfig((prev) => ({ ...prev, aspect_ratio }));
  }, []);

  const setQuality = useCallback((quality: string | null) => {
    setConfig((prev) => ({ ...prev, quality }));
  }, []);

  const setOutputFormat = useCallback((output_format: string) => {
    setConfig((prev) => ({ ...prev, output_format }));
  }, []);

  const setN = useCallback((n: number) => {
    setConfig((prev) => ({ ...prev, n }));
  }, []);

  const setBackground = useCallback((background: string | null) => {
    setConfig((prev) => ({ ...prev, background }));
  }, []);

  const setWebSearch = useCallback((web_search: boolean) => {
    setConfig((prev) => ({ ...prev, web_search }));
  }, []);

  const resetConfig = useCallback(() => {
    if (availableModels.length > 0) {
      const defaultModel = availableModels[0];
      setConfig({
        model: defaultModel.model_id,
        size: defaultModel.default_size,
        aspect_ratio: defaultModel.default_aspect_ratio || '1:1',
        quality: defaultModel.default_quality,
        output_format: defaultModel.default_output_format,
        n: 1,
        background: null,
        web_search: false,
      });
    } else {
      setConfig(DEFAULT_CONFIG);
    }
  }, [availableModels]);

  return {
    config,
    setConfig,
    currentModel,
    availableModels,
    setModel,
    setSize,
    setAspectRatio,
    setQuality,
    setOutputFormat,
    setN,
    setBackground,
    setWebSearch,
    resetConfig,
  };
}
